import {
  Badge,
  BrandMark,
  Button,
  Card,
  Icon,
  PageHeader,
  TwoFactorChallenge,
  useModalDialog,
} from "@kindtutor/web-ui";
import type { SchoolMemberRole } from "@kindtutor/api-contract";
import {
  fetchAccessContext,
  getAuthErrorMessage,
  isSafeReturnPath,
} from "@kindtutor/auth-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { apiBaseURL, apiFetch, authClient } from "./auth-client";
import {
  acceptSchoolInvitation,
  endHqSupportAccess,
  fetchSchoolLimits,
  fetchSchoolNotifications,
  fetchTeacherEscalations,
  fetchSchoolInvitation,
  markAllSchoolNotificationsRead,
  markSchoolNotificationRead,
} from "./api";
import { CurrentSchoolContext, useCurrentSchool } from "./school-context";
import { formatLastSeen } from "./formatters";

const Settings = lazy(() => import("./routes/SettingsRoute"));
const Onboarding = lazy(() => import("./routes/OnboardingRoute"));
const SchoolSupport = lazy(() => import("./routes/SupportRoute"));
const Materials = lazy(() => import("./routes/MaterialsRoute"));
const People = lazy(() => import("./routes/PeopleRoute"));
const Stations = lazy(() => import("./routes/StationsRoute"));
const Reports = lazy(() => import("./routes/ReportsRoute"));
const Dashboard = lazy(() => import("./routes/DashboardRoute"));

const navigation = [
  ["/", "Przegląd", "layout-dashboard"],
  ["/stations", "Stanowiska", "monitor"],
  ["/reports", "Raporty", "bar-chart"],
  ["/support", "Pomoc i zgłoszenia", "life-buoy"],
  ["/onboarding", "Start pilotażu", "rocket"],
  ["/curriculum", "Klasy i materiały", "book-open"],
  ["/users", "Osoby", "users"],
  ["/settings", "Ustawienia", "settings"],
] as const;

const schoolSearchItems = [
  { path: "/", label: "Przegląd", keywords: "dashboard podsumowanie" },
  {
    path: "/stations",
    label: "Stanowiska",
    keywords: "urządzenia tablety pairing",
  },
  {
    path: "/reports",
    label: "Raporty",
    keywords: "lekcje wykorzystanie oceny",
  },
  {
    path: "/support",
    label: "Pomoc i zgłoszenia",
    keywords: "incydenty nauczyciel",
  },
  {
    path: "/onboarding",
    label: "Start pilotażu",
    keywords: "gotowość konfiguracja",
  },
  {
    path: "/curriculum",
    label: "Klasy i materiały",
    keywords: "klasy przedmioty materiały",
  },
  { path: "/users", label: "Osoby", keywords: "użytkownicy nauczyciele" },
  { path: "/settings", label: "Ustawienia", keywords: "limity szkoła zasady" },
] as const;

const schoolRoleLabels: Record<SchoolMemberRole, string> = {
  school_admin: "Administrator szkoły",
  teacher: "Nauczyciel",
  technical_guardian: "Opiekun techniczny",
  viewer: "Podgląd",
};

function hqPanelUrl() {
  if (window.location.hostname === "localhost") return "http://localhost:5174";
  if (window.location.hostname.includes("staging")) {
    return "https://hq-staging.kindtutor.app";
  }
  return "https://hq.kindtutor.app";
}

function useSecureSignOut() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return async () => {
    queryClient.clear();
    await authClient.signOut();
    void navigate("/login", { replace: true });
  };
}

function useClearCacheOnIdentityChange(userId: string | undefined) {
  const queryClient = useQueryClient();
  const previousUserId = useRef<string | null>(null);

  useEffect(() => {
    const nextUserId = userId ?? null;
    if (previousUserId.current && previousUserId.current !== nextUserId) {
      queryClient.clear();
    }
    previousUserId.current = nextUserId;
  }, [queryClient, userId]);
}

function AppShell({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const secureSignOut = useSecureSignOut();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const sidebarRef = useModalDialog<HTMLElement>({
    open: menuOpen,
    onClose: () => setMenuOpen(false),
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { data: session } = authClient.useSession();
  const school = useCurrentSchool();
  const accountName = session?.user.name?.trim() || "Użytkownik";
  const accountInitials = accountName
    .split(/\s+/u)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toLocaleUpperCase("pl-PL");
  const limits = useQuery({
    queryKey: ["school-limits", school.schoolId],
    queryFn: () => fetchSchoolLimits(school.schoolId),
  });
  const escalations = useQuery({
    queryKey: ["teacher-escalations", school.schoolId],
    queryFn: () => fetchTeacherEscalations(school.schoolId),
    refetchInterval: 15_000,
  });
  const notifications = useQuery({
    queryKey: ["school-notifications", school.schoolId],
    queryFn: () => fetchSchoolNotifications(school.schoolId),
    refetchInterval: 30_000,
  });
  const markNotification = useMutation({
    mutationFn: (notificationId: string) =>
      markSchoolNotificationRead(school.schoolId, notificationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["school-notifications", school.schoolId],
      });
    },
  });
  const markAllNotifications = useMutation({
    mutationFn: () => markAllSchoolNotificationsRead(school.schoolId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["school-notifications", school.schoolId],
      });
    },
  });
  const endSupportAccess = useMutation({
    mutationFn: endHqSupportAccess,
    onSuccess: (data) => {
      window.location.assign(data.hqUrl);
    },
  });
  const openRequests = escalations.data?.escalations.filter(
    (item) => item.status === "open" || item.status === "acknowledged",
  );
  const notificationCount =
    (notifications.data?.unreadCount ?? 0) + (openRequests?.length ?? 0);
  const searchTerm = search.trim().toLocaleLowerCase("pl-PL");
  const searchResults =
    searchTerm.length >= 2
      ? schoolSearchItems.filter((item) =>
          `${item.label} ${item.keywords}`
            .toLocaleLowerCase("pl-PL")
            .includes(searchTerm),
        )
      : [];
  const current = navigation.find(([path]) =>
    path === "/"
      ? location.pathname === path
      : location.pathname.startsWith(path),
  );
  useEffect(() => {
    if (!school.impersonation) return;
    const remaining =
      new Date(school.impersonation.expiresAt).getTime() - Date.now();
    if (remaining <= 0) {
      window.location.assign(hqPanelUrl());
      return;
    }
    const timeout = window.setTimeout(
      () => window.location.assign(hqPanelUrl()),
      remaining,
    );
    return () => window.clearTimeout(timeout);
  }, [school.impersonation]);

  return (
    <div className="school-shell">
      <a className="skip-link" href="#main">
        Przejdź do treści
      </a>
      <aside
        ref={sidebarRef}
        className={`school-sidebar ${menuOpen ? "is-open" : ""}`}
      >
        <div className="brand">
          <BrandMark className="brand-mark" />
          <span>KindTutor</span>
        </div>
        <p className="school-name">{school.schoolName}</p>
        <nav aria-label="Główna nawigacja">
          {navigation.map(([path, label, icon]) => (
            <NavLink
              key={path}
              to={path}
              end={path === "/"}
              onClick={() => setMenuOpen(false)}
            >
              <span className="nav-icon" aria-hidden="true">
                <Icon name={icon} size={20} />
              </span>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <span>Wykorzystanie w tym miesiącu</span>
          <strong>
            {limits.data
              ? `${limits.data.usage.lessons.used.toLocaleString("pl-PL")} / ${limits.data.usage.lessons.limit.toLocaleString("pl-PL")} lekcji`
              : "Wczytywanie limitu…"}
          </strong>
        </div>
      </aside>
      {menuOpen ? (
        <button
          className="sidebar-scrim"
          aria-label="Zamknij menu"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}
      <div className="school-workspace">
        {school.impersonation ? (
          <div className="support-access-banner" role="status">
            <div>
              <strong>
                Tryb wsparcia HQ · {school.impersonation.actorName}
              </strong>
              <span>
                Dostęp do {school.schoolName} wygaśnie o{" "}
                {new Date(school.impersonation.expiresAt).toLocaleTimeString(
                  "pl-PL",
                  { hour: "2-digit", minute: "2-digit" },
                )}
                .
              </span>
            </div>
            <Button
              variant="secondary"
              onClick={() => endSupportAccess.mutate()}
              disabled={endSupportAccess.isPending}
            >
              {endSupportAccess.isPending ? "Kończenie…" : "Zakończ dostęp"}
            </Button>
          </div>
        ) : null}
        <header className="topbar">
          <button
            className="icon-button mobile-menu"
            aria-label={menuOpen ? "Zamknij menu" : "Otwórz menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <Icon name="menu" size={21} />
          </button>
          <strong>{current?.[1] ?? "Panel szkoły"}</strong>
          <div className="global-search">
            <label className="sr-only" htmlFor="school-global-search">
              Szukaj w panelu
            </label>
            <Icon name="search" size={21} />
            <input
              id="school-global-search"
              type="search"
              aria-label="Szukaj w panelu"
              placeholder="Szukaj sekcji w panelu"
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && searchResults[0]) {
                  void navigate(searchResults[0].path);
                  setSearch("");
                }
              }}
            />
            {searchTerm.length >= 2 ? (
              <Card className="school-search-results">
                {searchResults.length ? (
                  searchResults.map((result) => (
                    <button
                      type="button"
                      key={result.path}
                      onClick={() => {
                        void navigate(result.path);
                        setSearch("");
                      }}
                    >
                      <strong>{result.label}</strong>
                      <small>Otwórz sekcję</small>
                    </button>
                  ))
                ) : (
                  <p>Brak sekcji pasującej do „{search.trim()}”.</p>
                )}
              </Card>
            ) : null}
          </div>
          <div className="topbar-actions">
            <button
              className="icon-button"
              aria-label={`Powiadomienia, ${notificationCount} nowych`}
              onClick={() => {
                setSearch("");
                setNotificationsOpen((open) => !open);
              }}
            >
              <Icon name="bell" size={21} />
              {notificationCount > 0 ? (
                <span className="notification-dot">{notificationCount}</span>
              ) : null}
            </button>
            <button
              className="profile-button"
              aria-label="Menu użytkownika"
              aria-expanded={profileOpen}
              onClick={() => {
                setNotificationsOpen(false);
                setSearch("");
                setProfileOpen((open) => !open);
              }}
            >
              <span>{accountInitials}</span>
              <span className="profile-copy">
                <strong>{accountName}</strong>
                <small>{schoolRoleLabels[school.role]}</small>
              </span>
            </button>
          </div>
          {notificationsOpen ? (
            <Card className="notification-popover">
              <div className="popover-heading">
                <strong>Powiadomienia</strong>
                <button
                  disabled={
                    !notifications.data?.unreadCount ||
                    markAllNotifications.isPending
                  }
                  onClick={() => markAllNotifications.mutate()}
                >
                  Przeczytaj wszystkie
                </button>
              </div>
              {notifications.isPending ? (
                <p className="notification-empty" role="status">
                  Wczytujemy powiadomienia…
                </p>
              ) : null}
              {notifications.data?.notifications.map((notification) => (
                <Link
                  to={notification.path}
                  key={notification.id}
                  className={notification.readAt ? "is-read" : ""}
                  onClick={() => {
                    if (!notification.readAt) {
                      markNotification.mutate(notification.id);
                    }
                    setNotificationsOpen(false);
                  }}
                >
                  <span className="notice-icon" aria-hidden="true">
                    {notification.severity === "critical" ? "!" : "✓"}
                  </span>
                  <span>
                    {notification.title}
                    <small>{notification.body}</small>
                    <small>{formatLastSeen(notification.createdAt)}</small>
                  </span>
                </Link>
              ))}
              {openRequests?.length
                ? openRequests.slice(0, 5).map((item) => (
                    <Link
                      to="/reports#teacher-requests"
                      key={item.id}
                      onClick={() => setNotificationsOpen(false)}
                    >
                      <span className="notice-icon" aria-hidden="true">
                        !
                      </span>
                      <span>
                        {item.stationName} prosi o nauczyciela
                        <small>
                          {item.gradeName} · {item.subjectName} ·{" "}
                          {formatLastSeen(item.createdAt)}
                        </small>
                      </span>
                    </Link>
                  ))
                : null}
              {!notifications.isPending &&
              notifications.data?.notifications.length === 0 &&
              !openRequests?.length ? (
                <p className="notification-empty">Brak nowych próśb uczniów.</p>
              ) : null}
            </Card>
          ) : null}
          {profileOpen ? (
            <Card className="profile-popover">
              <strong>{accountName}</strong>
              <span>{session?.user.email}</span>
              <Link to="/change-password" onClick={() => setProfileOpen(false)}>
                Zmień hasło
              </Link>
              <button
                onClick={() => {
                  void (async () => {
                    await secureSignOut();
                    setProfileOpen(false);
                  })();
                }}
              >
                Wyloguj się
              </button>
            </Card>
          ) : null}
        </header>
        <main id="main" className="school-content" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}

function Login() {
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session, isPending: sessionPending } = authClient.useSession();

  if (!sessionPending && session) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setPending(true);
    const data = new FormData(event.currentTarget);
    const result = await authClient.signIn.email({
      email: String(data.get("email") ?? "").trim(),
      password: String(data.get("password") ?? ""),
      rememberMe: true,
    });
    setPending(false);

    if (result.error) {
      setError(getAuthErrorMessage(result.error));
      return;
    }

    const returnTo = (location.state as { from?: unknown } | null)?.from;
    void navigate(isSafeReturnPath(returnTo) ? returnTo : "/", {
      replace: true,
    });
  }

  return (
    <main className="auth-page">
      <Card className="auth-card">
        <div className="brand auth-brand">
          <BrandMark className="brand-mark" />
          <span>KindTutor</span>
        </div>
        <h1>Zaloguj się do panelu szkoły</h1>
        <p>Zarządzaj stanowiskami, materiałami i raportami.</p>
        <form onSubmit={(event) => void handleSubmit(event)}>
          <label>
            Adres e-mail
            <input name="email" type="email" autoComplete="email" required />
          </label>
          <label>
            Hasło
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              required
            />
          </label>
          {error ? (
            <p className="auth-message auth-message--error" role="alert">
              {error}
            </p>
          ) : null}
          <Link to="/forgot-password">Nie pamiętam hasła</Link>
          <Button type="submit" disabled={pending || sessionPending}>
            {pending ? "Logowanie…" : "Zaloguj się"}
          </Button>
        </form>
        <small>
          Potrzebujesz pomocy?{" "}
          <a href="mailto:hi@kindtutor.app">hi@kindtutor.app</a>
        </small>
      </Card>
    </main>
  );
}

function TwoFactorLogin() {
  const navigate = useNavigate();
  const verify = async (
    operation: Promise<{ error: { code?: string; status?: number } | null }>,
  ) => {
    const result = await operation;
    if (result.error) throw new Error(getAuthErrorMessage(result.error));
  };
  return (
    <main className="auth-page">
      <Card className="auth-card">
        <TwoFactorChallenge
          onVerifyCode={(code, trustDevice) =>
            verify(authClient.twoFactor.verifyTotp({ code, trustDevice }))
          }
          onVerifyBackupCode={(code, trustDevice) =>
            verify(
              authClient.twoFactor.verifyBackupCode({
                code,
                trustDevice,
              }),
            )
          }
          onVerified={() => void navigate("/", { replace: true })}
        />
      </Card>
    </main>
  );
}

function ForgotPassword() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setPending(true);
    const data = new FormData(event.currentTarget);
    const result = await authClient.requestPasswordReset({
      email: String(data.get("email") ?? "").trim(),
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setPending(false);
    if (result.error) {
      setError(getAuthErrorMessage(result.error));
      return;
    }
    setSent(true);
  }

  return (
    <main className="auth-page">
      <Card className="auth-card">
        <div className="brand auth-brand">
          <BrandMark className="brand-mark" />
          <span>KindTutor</span>
        </div>
        <h1>Przypomnij hasło</h1>
        <p>Wyślemy bezpieczny link na adres przypisany do konta.</p>
        {sent ? (
          <div className="auth-success" role="status">
            <strong>Sprawdź swoją skrzynkę</strong>
            <p>
              Jeśli konto istnieje, wysłaliśmy wiadomość z dalszymi krokami.
            </p>
          </div>
        ) : (
          <form onSubmit={(event) => void handleSubmit(event)}>
            <label>
              Adres e-mail
              <input name="email" type="email" autoComplete="email" required />
            </label>
            {error ? (
              <p className="auth-message auth-message--error" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" disabled={pending}>
              {pending ? "Wysyłanie…" : "Wyślij link"}
            </Button>
          </form>
        )}
        <Link className="back-link" to="/login">
          ← Wróć do logowania
        </Link>
      </Card>
    </main>
  );
}

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    const data = new FormData(event.currentTarget);
    const password = String(data.get("password") ?? "");
    const confirmation = String(data.get("confirmation") ?? "");
    if (password !== confirmation) {
      setError("Hasła nie są takie same.");
      return;
    }
    setPending(true);
    setError("");
    const result = await authClient.resetPassword({
      newPassword: password,
      token,
    });
    setPending(false);
    if (result.error) {
      setError(getAuthErrorMessage(result.error));
      return;
    }
    setDone(true);
  }

  return (
    <main className="auth-page">
      <Card className="auth-card">
        <div className="brand auth-brand">
          <BrandMark className="brand-mark" />
          <span>KindTutor</span>
        </div>
        <h1>Ustaw nowe hasło</h1>
        {!token ? (
          <div className="auth-message auth-message--error" role="alert">
            Ten link jest nieprawidłowy. Poproś o nową wiadomość.
          </div>
        ) : done ? (
          <div className="auth-success" role="status">
            <strong>Hasło zostało zmienione</strong>
            <Link to="/login">Przejdź do logowania</Link>
          </div>
        ) : (
          <form onSubmit={(event) => void handleSubmit(event)}>
            <label>
              Nowe hasło
              <input
                name="password"
                type="password"
                minLength={12}
                maxLength={128}
                autoComplete="new-password"
                required
              />
              <small>Co najmniej 12 znaków.</small>
            </label>
            <label>
              Powtórz nowe hasło
              <input
                name="confirmation"
                type="password"
                minLength={12}
                maxLength={128}
                autoComplete="new-password"
                required
              />
            </label>
            {error ? (
              <p className="auth-message auth-message--error" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" disabled={pending}>
              {pending ? "Zapisywanie…" : "Zapisz nowe hasło"}
            </Button>
          </form>
        )}
      </Card>
    </main>
  );
}

function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [acceptedEmail, setAcceptedEmail] = useState("");
  const invitation = useQuery({
    queryKey: ["school-invitation", token],
    queryFn: () => fetchSchoolInvitation(token),
    enabled: token.length >= 32,
    retry: false,
  });

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!invitation.data) return;
    const data = new FormData(event.currentTarget);
    const displayName = String(data.get("displayName") ?? "").trim();
    const password = String(data.get("password") ?? "");
    const confirmation = String(data.get("confirmation") ?? "");
    if (displayName.length < 2) {
      setError("Wpisz imię i nazwisko.");
      return;
    }
    if (password.length < 12) {
      setError("Hasło musi mieć co najmniej 12 znaków.");
      return;
    }
    if (password !== confirmation) {
      setError("Hasła nie są takie same.");
      return;
    }
    setPending(true);
    setError("");
    try {
      const result = await acceptSchoolInvitation({
        token,
        displayName,
        password,
      });
      setAcceptedEmail(result.loginEmail);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Nie udało się przyjąć zaproszenia.",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="auth-page">
      <Card className="auth-card invitation-card">
        <div className="brand auth-brand">
          <BrandMark className="brand-mark" />
          <span>KindTutor</span>
        </div>
        {!token || token.length < 32 ? (
          <>
            <h1>Link jest nieprawidłowy</h1>
            <p>Poproś administratora szkoły o nowe zaproszenie.</p>
          </>
        ) : invitation.isPending ? (
          <div className="auth-loading auth-loading--inline" aria-live="polite">
            <span className="auth-spinner" />
            <p>Sprawdzamy zaproszenie…</p>
          </div>
        ) : invitation.isError || !invitation.data ? (
          <>
            <h1>Zaproszenie jest nieaktywne</h1>
            <div className="auth-message auth-message--error" role="alert">
              Zaproszenie wygasło, zostało anulowane albo już go użyto.
            </div>
            <p>Poproś administratora szkoły o wysłanie nowego linku.</p>
          </>
        ) : acceptedEmail ? (
          <>
            <h1>Konto jest gotowe</h1>
            <div className="auth-success" role="status">
              <strong>Dołączono do {invitation.data.schoolName}</strong>
              <p>Zaloguj się adresem {acceptedEmail} i ustawionym hasłem.</p>
            </div>
            <Link className="auth-primary-link" to="/login">
              Przejdź do logowania
            </Link>
          </>
        ) : (
          <>
            <Badge tone="info">Zaproszenie do panelu szkoły</Badge>
            <h1>Dołącz do {invitation.data.schoolName}</h1>
            <p>
              Konto zostanie utworzone dla{" "}
              <strong>{invitation.data.email}</strong>. Twoja rola:{" "}
              {schoolRoleLabels[invitation.data.role]}.
            </p>
            <form onSubmit={(event) => void submit(event)} noValidate>
              <label>
                Imię i nazwisko
                <input
                  name="displayName"
                  defaultValue={invitation.data.displayName}
                  autoComplete="name"
                  minLength={2}
                  maxLength={200}
                  required
                />
              </label>
              <label>
                Hasło
                <input
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  required
                />
                <small>Co najmniej 12 znaków.</small>
              </label>
              <label>
                Powtórz hasło
                <input
                  name="confirmation"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  required
                />
              </label>
              {error ? (
                <p className="auth-message auth-message--error" role="alert">
                  {error}
                </p>
              ) : null}
              <Button type="submit" disabled={pending}>
                {pending ? "Tworzenie konta…" : "Przyjmij zaproszenie"}
              </Button>
            </form>
          </>
        )}
        <small>
          Potrzebujesz pomocy?{" "}
          <a href="mailto:hi@kindtutor.app">hi@kindtutor.app</a>
        </small>
      </Card>
    </main>
  );
}

function ChangePassword() {
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const newPassword = String(data.get("newPassword") ?? "");
    const confirmation = String(data.get("confirmation") ?? "");
    if (newPassword !== confirmation) {
      setError("Nowe hasła nie są takie same.");
      return;
    }
    setPending(true);
    setDone(false);
    setError("");
    const result = await authClient.changePassword({
      currentPassword: String(data.get("currentPassword") ?? ""),
      newPassword,
      revokeOtherSessions: true,
    });
    setPending(false);
    if (result.error) {
      setError(getAuthErrorMessage(result.error));
      return;
    }
    form.reset();
    setDone(true);
  }

  return (
    <>
      <PageHeader
        title="Zmień hasło"
        description="Po zmianie hasła pozostałe urządzenia zostaną wylogowane."
      />
      <Card className="password-card">
        <form
          className="password-form"
          onSubmit={(event) => void handleSubmit(event)}
        >
          <label>
            Obecne hasło
            <input
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              required
            />
          </label>
          <label>
            Nowe hasło
            <input
              name="newPassword"
              type="password"
              minLength={12}
              maxLength={128}
              autoComplete="new-password"
              required
            />
            <small>Co najmniej 12 znaków.</small>
          </label>
          <label>
            Powtórz nowe hasło
            <input
              name="confirmation"
              type="password"
              minLength={12}
              maxLength={128}
              autoComplete="new-password"
              required
            />
          </label>
          {error ? (
            <p className="auth-message auth-message--error" role="alert">
              {error}
            </p>
          ) : null}
          {done ? (
            <p className="auth-message auth-message--success" role="status">
              Hasło zostało zmienione.
            </p>
          ) : null}
          <Button type="submit" disabled={pending}>
            {pending ? "Zapisywanie…" : "Zmień hasło"}
          </Button>
        </form>
      </Card>
    </>
  );
}

function RequireSession({ children }: { children: ReactNode }) {
  const secureSignOut = useSecureSignOut();
  const { data: session, isPending } = authClient.useSession();
  useClearCacheOnIdentityChange(session?.user.id);
  const access = useQuery({
    queryKey: ["access-context", session?.user.id],
    queryFn: () => fetchAccessContext(apiBaseURL, apiFetch),
    enabled: Boolean(session),
    staleTime: 30_000,
  });
  const location = useLocation();
  if (isPending) {
    return (
      <main className="auth-loading" aria-live="polite">
        <span className="auth-spinner" />
        <p>Sprawdzamy dostęp…</p>
      </main>
    );
  }
  if (!session) {
    return (
      <Navigate
        to="/login"
        state={{ from: `${location.pathname}${location.search}` }}
        replace
      />
    );
  }
  if (access.isPending) {
    return (
      <main className="auth-loading" aria-live="polite">
        <span className="auth-spinner" />
        <p>Sprawdzamy szkołę i uprawnienia…</p>
      </main>
    );
  }
  if (access.isError) {
    return (
      <main className="auth-page">
        <Card className="auth-card">
          <h1>Nie udało się sprawdzić dostępu</h1>
          <p>Odśwież stronę lub spróbuj ponownie za chwilę.</p>
          <Button onClick={() => void access.refetch()}>
            Spróbuj ponownie
          </Button>
        </Card>
      </main>
    );
  }
  const school = access.data?.schoolAccess[0];
  if (!school) {
    return (
      <main className="auth-page">
        <Card className="auth-card">
          <h1>Brak dostępu do szkoły</h1>
          <p>To konto nie jest przypisane do aktywnej szkoły.</p>
          <Button
            onClick={() => {
              void (async () => {
                await secureSignOut();
              })();
            }}
          >
            Zaloguj się innym kontem
          </Button>
        </Card>
      </main>
    );
  }
  return (
    <CurrentSchoolContext.Provider
      value={{
        ...school,
        impersonation: access.data?.impersonation ?? null,
      }}
    >
      {children}
    </CurrentSchoolContext.Provider>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/two-factor" element={<TwoFactorLogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route
        path="*"
        element={
          <RequireSession>
            <AppShell>
              <Suspense fallback={<p role="status">Wczytywanie widoku…</p>}>
                <Routes>
                  <Route index element={<Dashboard />} />
                  <Route path="stations" element={<Stations />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="support" element={<SchoolSupport />} />
                  <Route path="onboarding" element={<Onboarding />} />
                  <Route path="curriculum" element={<Materials />} />
                  <Route path="users" element={<People />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="change-password" element={<ChangePassword />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </AppShell>
          </RequireSession>
        }
      />
    </Routes>
  );
}
